import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { sellersAPI, trackingAPI, followsAPI, postsAPI, accessAPI } from '../../services/api';
import { messagingService } from '../../services/messagingService';
import {
  Building2,
  MapPin,
  Globe,
  Calendar,
  ArrowLeft,
  FlaskConical,
  ExternalLink,
  Mail,
  Loader2,
  FileText,
  Download,
  Users,
  DollarSign,
  TrendingUp,
  Lock,
  Target,
  Eye,
  EyeOff,
  Star,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  BarChart3,
  Zap,
  Shield,
  Key,
  Unlock,
  AlertCircle,
  X,
} from 'lucide-react';

import { supabase } from '../../lib/supabase';

// API base URL (without /api - we add it explicitly in each endpoint)
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Helper to get auth token from Supabase session
const getAuthToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (err) {
    console.warn('Failed to get auth token:', err);
    return null;
  }
};

// Document type labels for display
const DOCUMENT_LABELS = {
  nda_template: 'NDA Template',
  corporate_deck: 'Corporate Presentation',
  teaser: 'Non-Confidential Teaser',
  investigator_brochure: "Investigator's Brochure",
  clinical_data_package: 'Clinical Data Package',
  patent_summary: 'Patent Portfolio Summary',
  cmc_overview: 'CMC Overview',
  regulatory_status: 'Regulatory Filing Status',
};

// Post type configuration matching backend POST_TYPES
const POST_TYPE_CONFIG = {
  pipeline_milestone: { label: 'Pipeline Milestone', color: 'bg-purple-100 text-purple-700', iconColor: 'text-purple-600', bgColor: 'bg-purple-100' },
  partnership_news: { label: 'Partnership', color: 'bg-blue-100 text-blue-700', iconColor: 'text-blue-600', bgColor: 'bg-blue-100' },
  fundraising: { label: 'Fundraising', color: 'bg-green-100 text-green-700', iconColor: 'text-green-600', bgColor: 'bg-green-100' },
  clinical_data: { label: 'Clinical Data', color: 'bg-teal-100 text-teal-700', iconColor: 'text-teal-600', bgColor: 'bg-teal-100' },
  regulatory: { label: 'Regulatory', color: 'bg-orange-100 text-orange-700', iconColor: 'text-orange-600', bgColor: 'bg-orange-100' },
  team_update: { label: 'Team Update', color: 'bg-indigo-100 text-indigo-700', iconColor: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  conference: { label: 'Conference', color: 'bg-pink-100 text-pink-700', iconColor: 'text-pink-600', bgColor: 'bg-pink-100' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-600', iconColor: 'text-gray-600', bgColor: 'bg-gray-100' },
};

// Get post type config
const getPostTypeConfig = (postType) => {
  return POST_TYPE_CONFIG[postType] || POST_TYPE_CONFIG.general;
};

// Stage colors mapping
const stageColors = {
  'Discovery': 'bg-blue-100 text-blue-700 border-blue-200',
  'Preclinical': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Phase 1': 'bg-purple-100 text-purple-700 border-purple-200',
  'Phase 2': 'bg-pink-100 text-pink-700 border-pink-200',
  'Phase 3': 'bg-orange-100 text-orange-700 border-orange-200',
  'Approved': 'bg-green-100 text-green-700 border-green-200',
};

// Stage indicator component
const StageIndicator = ({ stage }) => {
  const colorClass = stageColors[stage] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${colorClass}`}>
      {stage}
    </span>
  );
};

// Document download component for buyers
function DocumentItem({ doc, docType }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!doc?.filePath) return;

    setDownloading(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const params = new URLSearchParams({
        filePath: doc.filePath,
        fileName: doc.fileName || 'download'
      });
      const response = await fetch(
        `${API_URL}/api/storage/signed-url?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get download URL');
      }

      const result = await response.json();

      // Fetch the file as blob for reliable cross-origin download
      const fileResponse = await fetch(result.data.signedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-blue-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {DOCUMENT_LABELS[docType] || docType}
          </p>
          <p className="text-xs text-gray-500 truncate">{doc.fileName}</p>
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="p-2 hover:bg-blue-100 rounded-lg disabled:opacity-50 flex-shrink-0 transition-colors"
        title="Download"
      >
        {downloading ? (
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        ) : (
          <Download className="w-4 h-4 text-blue-600" />
        )}
      </button>
    </div>
  );
}

// Quick info pill component
const InfoPill = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg ${className}`}>
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

export default function SellerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [seller, setSeller] = useState(null);
  const [startingConversation, setStartingConversation] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [copied, setCopied] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessRequestMessage, setAccessRequestMessage] = useState('');
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('interested');

  // Check if user is a buyer
  const isBuyer = userProfile?.user_type === 'buyer';
  const isAuthenticated = !!user;

  // Access control info from seller response (backend returns directly on seller object)
  const currentAccessLevel = seller?.accessLevel || 'public';
  const canRequestAccess = seller?.canRequestAccess && isBuyer;
  const pendingRequest = seller?.pendingRequest;
  const hasFullAccess = seller?.fullAccess || false;

  // Check if user has at least "interested" level access (can see funding, employees)
  const hasInterestedAccess = ['interested', 'deep_dive', 'deal_terms'].includes(currentAccessLevel);

  const viewStartTime = useRef(Date.now());
  const pagesViewed = useRef(['overview']);
  const buyerId = userProfile?.buyer_id;
  const buyerIdRef = useRef(buyerId);
  const hasTrackedView = useRef(false);

  // Keep buyerIdRef updated
  useEffect(() => {
    buyerIdRef.current = buyerId;
  }, [buyerId]);

  useEffect(() => {
    loadSeller();
  }, [id]);

  // Track view when buyerId becomes available (user is logged in)
  useEffect(() => {
    if (buyerId && id && !hasTrackedView.current) {
      // Track initial view with minimal duration
      hasTrackedView.current = true;
      trackingAPI.recordView({
        seller_id: id,
        buyer_id: buyerId,
        view_duration: 1,
        pages_viewed: ['overview'],
      }).catch(err => console.debug('Initial view tracking failed:', err));
    }

    // Track extended view on unmount using the ref for latest buyerId
    return () => {
      const currentBuyerId = buyerIdRef.current;
      if (currentBuyerId && id) {
        const duration = Math.floor((Date.now() - viewStartTime.current) / 1000);
        // Use sendBeacon for reliable tracking on page unload
        const data = JSON.stringify({
          seller_id: id,
          buyer_id: currentBuyerId,
          view_duration: duration,
          pages_viewed: pagesViewed.current,
        });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon?.(`${API_URL}/api/tracking/view`, blob);
      }
    };
  }, [buyerId, id]);

  // Check follow status and get follower count
  useEffect(() => {
    const checkFollowAndCount = async () => {
      try {
        const countResponse = await followsAPI.getFollowerCount(id);
        setFollowerCount(countResponse.data?.data?.count || 0);

        if (isBuyer) {
          const statusResponse = await followsAPI.checkStatus(id);
          setIsFollowing(statusResponse.data?.data?.isFollowing || false);
        }
      } catch (error) {
        console.debug('Failed to check follow status:', error);
      }
    };

    if (id) {
      checkFollowAndCount();
    }
  }, [id, isBuyer]);

  // Load seller's posts
  useEffect(() => {
    const loadPosts = async () => {
      setPostsLoading(true);
      try {
        const response = await postsAPI.getBySellerForPublic(id, { limit: 10 });
        setPosts(response.data?.data?.posts || []);
      } catch (error) {
        console.debug('Failed to load posts:', error);
      } finally {
        setPostsLoading(false);
      }
    };

    if (id) {
      loadPosts();
    }
  }, [id]);

  const loadSeller = async () => {
    try {
      const response = await sellersAPI.getById(id);
      setSeller(response.data?.data);
    } catch (error) {
      console.error('Failed to load seller:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackView = async (duration) => {
    // Only track views for logged-in buyers
    if (!buyerId) return;

    try {
      await trackingAPI.recordView({
        seller_id: id,
        buyer_id: buyerId,
        view_duration: duration,
        pages_viewed: pagesViewed.current,
      });
    } catch (error) {
      console.debug('View tracking failed:', error);
    }
  };

  const handleStartConversation = async () => {
    if (!isBuyer || !seller?.id) return;

    setStartingConversation(true);
    try {
      const conversationId = await messagingService.getOrCreateConversation(
        seller.id,
        buyerId,
        `Interest in ${seller.company_name}`
      );
      navigate(`/buyer/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setStartingConversation(false);
    }
  };

  const handleFollow = async () => {
    if (!isBuyer || followLoading) return;

    if (!buyerId) {
      alert('Your buyer profile is incomplete. Please contact support.');
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followsAPI.unfollow(id);
        setIsFollowing(false);
        setFollowerCount((prev) => Math.max(0, prev - 1));
      } else {
        await followsAPI.follow(id);
        setIsFollowing(true);
        setFollowerCount((prev) => prev + 1);
      }

      // Reload posts to reflect new follow status (buyers_only visibility)
      try {
        const response = await postsAPI.getBySellerForPublic(id, { limit: 10 });
        setPosts(response.data?.data?.posts || []);
      } catch (err) {
        console.debug('Failed to reload posts:', err);
      }
    } catch (error) {
      console.error('Failed to update follow status:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update follow status';
      alert(`Error: ${errorMessage}`);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRequestAccess = async () => {
    if (!isBuyer || requestingAccess) return;

    setRequestingAccess(true);
    try {
      await accessAPI.requestAccess(id, {
        accessLevel: selectedAccessLevel,
        message: accessRequestMessage,
      });
      // Reload seller to get updated access info
      await loadSeller();
      setShowAccessModal(false);
      setAccessRequestMessage('');
    } catch (error) {
      console.error('Failed to request access:', error);
      alert(error.response?.data?.error || 'Failed to submit access request');
    } finally {
      setRequestingAccess(false);
    }
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      public: 'Public',
      interested: 'Interested Buyer',
      deep_dive: 'Deep Dive',
      deal_terms: 'Full Deal Terms',
    };
    return labels[level] || level;
  };

  const getNextAccessLevel = () => {
    const levels = ['public', 'interested', 'deep_dive', 'deal_terms'];
    const currentIndex = levels.indexOf(currentAccessLevel);
    if (currentIndex < levels.length - 1) {
      return levels[currentIndex + 1];
    }
    return null;
  };

  const toggleAssetExpand = (assetId) => {
    setExpandedAssets(prev => ({
      ...prev,
      [assetId]: !prev[assetId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading company profile...</p>
        </div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">Company not found</p>
          <Link to="/buyer/search" className="text-blue-600 hover:underline">
            Back to search
          </Link>
        </div>
      </div>
    );
  }

  const displayedPosts = showAllPosts ? posts : posts.slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link
                to="/buyer/search"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                  {seller.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                </div>
                <div className="hidden sm:block">
                  <h1 className="font-semibold text-gray-900 truncate max-w-[200px]">
                    {seller.company_name}
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy link"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {/* Follow Button */}
              {isBuyer ? (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors ${
                    isFollowing
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {followLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isFollowing ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                  {isFollowing ? 'Watching' : 'Watch'}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Watch
                </Link>
              )}

              {/* Message Button */}
              {isBuyer && (
                <button
                  onClick={handleStartConversation}
                  disabled={startingConversation}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium text-sm transition-colors"
                >
                  {startingConversation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Message</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Overview Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Company Header */}
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {seller.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h1 className="text-xl font-bold text-gray-900">
                          {seller.company_name}
                        </h1>
                        {seller.company_name_cn && (
                          <p className="text-gray-600">{seller.company_name_cn}</p>
                        )}
                      </div>
                      {seller.stage && <StageIndicator stage={seller.stage} />}
                    </div>

                    {/* Quick Info Row */}
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-600">
                      {seller.headquarters && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {seller.headquarters}
                        </span>
                      )}
                      {seller.founded && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          Est. {seller.founded}
                        </span>
                      )}
                      {seller.website && (
                        <a
                          href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                        >
                          <Globe className="w-4 h-4" />
                          Website
                          <ArrowUpRight className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {seller.description && (
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {seller.description}
                  </p>
                )}

                {/* Therapeutic Areas & Modalities */}
                {(seller.therapeutic_areas?.length > 0 || seller.modalities?.length > 0) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {seller.therapeutic_areas?.map((area, idx) => (
                      <span key={`ta-${idx}`} className="px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
                        {area}
                      </span>
                    ))}
                    {seller.modalities?.map((mod, idx) => (
                      <span key={`mod-${idx}`} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                        {mod}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Company Metrics - Show if user has at least "interested" level access */}
              {hasInterestedAccess && (seller.employees || seller.funding_total || seller.funding_stage || seller.stock_ticker) && (
                <div className="px-6 pb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {seller.employees && (
                      <InfoPill icon={Users} label="Employees" value={seller.employees} />
                    )}
                    {seller.funding_total && (
                      <InfoPill icon={DollarSign} label="Total Funding" value={seller.funding_total} />
                    )}
                    {seller.funding_stage && (
                      <InfoPill icon={TrendingUp} label="Funding Stage" value={seller.funding_stage} />
                    )}
                    {seller.stock_ticker && (
                      <InfoPill icon={BarChart3} label="Ticker" value={seller.stock_ticker} />
                    )}
                  </div>
                </div>
              )}

              {/* Prompt for non-authenticated users */}
              {!isAuthenticated && (
                <div className="px-6 pb-6">
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                    <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Sign in to Connect</p>
                      <p className="text-xs text-amber-600">Create an account to message this company and request detailed information</p>
                    </div>
                    <Link to="/login" className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors flex-shrink-0">
                      Sign In
                    </Link>
                  </div>
                </div>
              )}

              {/* Prompt for authenticated buyers without "interested" access to request more */}
              {isBuyer && !hasInterestedAccess && canRequestAccess && !pendingRequest && (
                <div className="px-6 pb-6">
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <Key className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">Request Access for More Details</p>
                      <p className="text-xs text-blue-600">View funding details, clinical data, and deal terms</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedAccessLevel('interested');
                        setShowAccessModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex-shrink-0"
                    >
                      Request Access
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Pipeline Assets */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FlaskConical className="w-5 h-5 text-blue-600" />
                    Pipeline Assets
                  </h2>
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    {seller.assets?.length || 0} assets
                  </span>
                </div>
              </div>

              {seller.assets && seller.assets.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {seller.assets.map((asset) => (
                    <div key={asset.id} className="p-4">
                      {/* Asset Header */}
                      <div
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => toggleAssetExpand(asset.id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">
                              {asset.asset_name}
                            </h3>
                            <StageIndicator stage={asset.stage} />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {asset.modality && (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                                {asset.modality}
                              </span>
                            )}
                            {asset.target && (
                              <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full">
                                {asset.target}
                              </span>
                            )}
                            {asset.indication && (
                              <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">
                                {asset.indication}
                              </span>
                            )}
                          </div>
                        </div>
                        <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                          {expandedAssets[asset.id] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>

                      {/* Expanded Details */}
                      {expandedAssets[asset.id] && (
                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                          {asset.differentiation && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Differentiation
                              </p>
                              <p className="text-sm text-gray-700">{asset.differentiation}</p>
                            </div>
                          )}

                          {asset.clinical_data && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Clinical Data
                              </p>
                              <p className="text-sm text-gray-700">{asset.clinical_data}</p>
                            </div>
                          )}

                          {/* Deal Terms - Only for authenticated users */}
                          {hasFullAccess && (asset.deal_preferences || asset.upfront_range || asset.territory_rights) && (
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex items-center gap-2 mb-3">
                                <Target className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-semibold text-blue-800">Deal Terms</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {asset.deal_preferences && (
                                  <div>
                                    <p className="text-xs text-blue-600">Preferences</p>
                                    <p className="text-blue-900 font-medium">{asset.deal_preferences}</p>
                                  </div>
                                )}
                                {asset.upfront_range && (
                                  <div>
                                    <p className="text-xs text-blue-600">Upfront</p>
                                    <p className="text-blue-900 font-medium">{asset.upfront_range}</p>
                                  </div>
                                )}
                                {asset.milestone_range && (
                                  <div>
                                    <p className="text-xs text-blue-600">Milestones</p>
                                    <p className="text-blue-900 font-medium">{asset.milestone_range}</p>
                                  </div>
                                )}
                                {asset.royalty_range && (
                                  <div>
                                    <p className="text-xs text-blue-600">Royalties</p>
                                    <p className="text-blue-900 font-medium">{asset.royalty_range}</p>
                                  </div>
                                )}
                                {asset.territory_rights && (
                                  <div className="col-span-2">
                                    <p className="text-xs text-blue-600">Territory Rights</p>
                                    <p className="text-blue-900 font-medium">{asset.territory_rights}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No pipeline assets listed</p>
                </div>
              )}
            </div>

            {/* Company Updates */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Company Updates
                  </h2>
                  {isFollowing && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      <Star className="w-3 h-3" />
                      All Access
                    </span>
                  )}
                </div>
              </div>

              {/* Follow prompt for non-followers */}
              {isBuyer && !isFollowing && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Follow for Exclusive Updates</p>
                      <p className="text-xs text-amber-600">Some posts are only visible to followers</p>
                    </div>
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors flex-shrink-0"
                    >
                      {followLoading ? 'Following...' : 'Follow'}
                    </button>
                  </div>
                </div>
              )}

              {/* Follow prompt for non-authenticated users */}
              {!isAuthenticated && (
                <div className="p-4 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Sign In for Exclusive Content</p>
                      <p className="text-xs text-gray-500">Create an account and follow to see all updates</p>
                    </div>
                    <Link
                      to="/login"
                      className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors flex-shrink-0"
                    >
                      Sign In
                    </Link>
                  </div>
                </div>
              )}

              {postsLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500">Loading updates...</p>
                </div>
              ) : posts.length > 0 ? (
                <>
                  <div className="divide-y divide-gray-100">
                    {displayedPosts.map((post) => {
                      const typeConfig = getPostTypeConfig(post.post_type);
                      return (
                        <Link
                          key={post.id}
                          to={`/post/${post.id}`}
                          className="block p-4 hover:bg-gray-50 transition-colors group cursor-pointer"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConfig.bgColor}`}>
                              <FileText className={`w-5 h-5 ${typeConfig.iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeConfig.color}`}>
                                  {typeConfig.label}
                                </span>
                                {post.visibility === 'buyers_only' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                    <Star className="w-3 h-3" />
                                    Exclusive
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">
                                  {post.published_at && new Date(post.published_at).toLocaleDateString()}
                                </span>
                              </div>
                              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{post.title}</h3>
                              {(post.excerpt || post.content) && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {post.excerpt || post.content?.substring(0, 200)}
                                </p>
                              )}
                              <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                Read Full Post
                                <Eye className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  {posts.length > 3 && (
                    <div className="p-4 border-t border-gray-100">
                      <button
                        onClick={() => setShowAllPosts(!showAllPosts)}
                        className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {showAllPosts ? 'Show Less' : `View All ${posts.length} Updates`}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No updates posted yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>

              <div className="space-y-3">
                {/* Follow/Watch Button */}
                {isBuyer ? (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                      isFollowing
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {followLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isFollowing ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                    {isFollowing ? 'Watching' : 'Add to Watchlist'}
                    {followerCount > 0 && (
                      <span className="text-xs opacity-70">({followerCount})</span>
                    )}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Add to Watchlist
                  </Link>
                )}

                {/* Send Message - only for logged in buyers */}
                {isBuyer ? (
                  <button
                    onClick={handleStartConversation}
                    disabled={startingConversation}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
                  >
                    {startingConversation ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Send Message
                  </Link>
                )}

                {seller.website && (
                  <a
                    href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Visit Website
                  </a>
                )}
              </div>
            </div>

            {/* Access Level Card */}
            {isBuyer && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Your Access Level
                </h3>

                {/* Current Access Level */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                    currentAccessLevel === 'deal_terms' ? 'bg-green-100 text-green-700' :
                    currentAccessLevel === 'deep_dive' ? 'bg-blue-100 text-blue-700' :
                    currentAccessLevel === 'interested' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {currentAccessLevel === 'deal_terms' ? <Unlock className="w-4 h-4" /> :
                     currentAccessLevel === 'deep_dive' ? <Key className="w-4 h-4" /> :
                     currentAccessLevel === 'interested' ? <Eye className="w-4 h-4" /> :
                     <Lock className="w-4 h-4" />}
                    {getAccessLevelLabel(currentAccessLevel)}
                  </span>
                </div>

                {/* Pending Request Notice */}
                {pendingRequest && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100 mb-4">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Your <span className="font-medium">{getAccessLevelLabel(pendingRequest.access_level)}</span> access request is pending
                    </p>
                  </div>
                )}

                {/* Request Access Button - only show if not at max level and no pending request */}
                {canRequestAccess && !pendingRequest && currentAccessLevel !== 'deal_terms' && (
                  <button
                    onClick={() => {
                      const nextLevel = getNextAccessLevel();
                      if (nextLevel) {
                        setSelectedAccessLevel(nextLevel);
                        setShowAccessModal(true);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-lg font-medium transition-all shadow-sm"
                  >
                    <Key className="w-4 h-4" />
                    Request More Access
                  </button>
                )}

                {/* Access Level Info */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    {currentAccessLevel === 'deal_terms'
                      ? 'You have full access to all information'
                      : 'Request higher access to view more details like clinical data, deal terms, and documents.'}
                  </p>
                </div>
              </div>
            )}

            {/* Company Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                Engagement Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Pipeline Assets</span>
                  <span className="font-semibold text-gray-900">{seller.assets?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Watchers</span>
                  <span className="font-semibold text-gray-900">{followerCount}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Profile Views</span>
                  <span className="font-semibold text-gray-900">{seller.total_views || 0}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Recent Updates</span>
                  <span className="font-semibold text-gray-900">{posts.length}</span>
                </div>
              </div>
            </div>

            {/* Documents Card */}
            {seller.documents && Object.keys(seller.documents).length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Available Documents
                </h3>
                <div className="space-y-2">
                  {Object.entries(seller.documents).map(([docType, doc]) => (
                    <DocumentItem key={docType} docType={docType} doc={doc} />
                  ))}
                </div>
              </div>
            )}

            {/* Back to Search */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Explore More</h3>
              <p className="text-sm text-gray-600 mb-4">
                Discover similar companies in our database
              </p>
              <Link
                to="/buyer/search"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
              >
                <Zap className="w-4 h-4" />
                Search Pipeline
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Access Request Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Key className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Request Access</h2>
                  <p className="text-sm text-gray-500">{seller?.company_name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowAccessModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Access Level Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Level Requested
                </label>
                <div className="space-y-2">
                  {['interested', 'deep_dive', 'deal_terms'].map((level) => {
                    const isCurrentOrLower =
                      ['public', 'interested', 'deep_dive', 'deal_terms'].indexOf(level) <=
                      ['public', 'interested', 'deep_dive', 'deal_terms'].indexOf(currentAccessLevel);

                    return (
                      <label
                        key={level}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedAccessLevel === level
                            ? 'border-blue-500 bg-blue-50'
                            : isCurrentOrLower
                            ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="accessLevel"
                          value={level}
                          checked={selectedAccessLevel === level}
                          onChange={(e) => setSelectedAccessLevel(e.target.value)}
                          disabled={isCurrentOrLower}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{getAccessLevelLabel(level)}</p>
                          <p className="text-xs text-gray-500">
                            {level === 'interested' && 'View employee count, funding info'}
                            {level === 'deep_dive' && 'View clinical data, differentiation'}
                            {level === 'deal_terms' && 'View deal terms, documents'}
                          </p>
                        </div>
                        {isCurrentOrLower && (
                          <span className="text-xs text-green-600 font-medium">Already have</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message to Seller (Optional)
                </label>
                <textarea
                  value={accessRequestMessage}
                  onChange={(e) => setAccessRequestMessage(e.target.value)}
                  placeholder="Introduce yourself and explain your interest..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowAccessModal(false)}
                className="flex-1 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestAccess}
                disabled={requestingAccess}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {requestingAccess ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
